import { useBedrockRegistration } from 'store/bedrockRegistration';
import { decodeJWT } from 'store/utils';
import { useFeatureFlagContext } from 'lib/ContextTypes';
import { useVertical } from 'lib/Hooks/useVertical';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { HIDE_TABOOLA_ADS } from 'lib/brandFeatures';

/**
 * Gets subscription entitlements from the user's access token.
 *
 * @returns {string[]|null} Array of entitlement strings, or null if no access token
 */
export function getSubscriptionEntitlements() {
  const { access_token: accessToken } = useBedrockRegistration((state) => state.getAuthTokens());

  if (!accessToken) return null;

  const { entitlements } = decodeJWT(accessToken);
  // TODO: Handle entitlements when subscription products are available
  // Currently entitlements is an empty object {} when no subscriptions exist
  return Array.isArray(entitlements)
    ? entitlements
    : Object.keys(entitlements || {});
}

/**
 * FOR FUTURE USE:
 * Checks if the user has an ad-free subscription based on their entitlements.
 *
 * @returns {boolean} True if user has ad-free or premium subscription, false otherwise
 */
export function hasAdFreeSubscription() {
  const entitlements = getSubscriptionEntitlements();
  // Future logic when subscription API team provides the naming pattern
  return entitlements?.includes('ad-free') || entitlements?.includes('premium');
}

/**
 * Hook to check if ads should be hidden for the current user.
 * Currently hides ads for any logged-in user when subscription feature flag is enabled.
 * TODO: Update to use hasAdFreeSubscription() when entitlements are implemented.
 *
 * @returns {boolean} True if ads should be hidden, false otherwise
 */
export function useShouldRemoveAds() {
  const featureFlags = useFeatureFlagContext();
  const { access_token: accessToken } = useBedrockRegistration(
    (state) => state.getAuthTokens(),
  );

  return featureFlags?.['nbc-news-subscriptions'] && accessToken;
}

/**
 * Hook to check if Taboola ads should be removed for the current user.
 * - Uses brand-level feature config (HIDE_TABOOLA_ADS) to determine if Taboola should be removed.
 * - Checks if subscriptions feature flag is enabled.
 * - Ensures the "show-taboola-for-subscribers" override is NOT set (show taboola regardless of subscription when true).
 * - Currently hides ads for any logged-in user
 * - TODO: Update to use hasAdFreeSubscription() when entitlements are implemented.
 *
 * @returns {boolean} True if Taboola ads should be hidden, false otherwise.
 */
export function useShouldRemoveTaboolaAds() {
  const featureFlags = useFeatureFlagContext();
  const { vertical } = useVertical();
  const { access_token: accessToken } = useBedrockRegistration(
    (state) => state.getAuthTokens(),
  );

  return Boolean(
    getFeatureConfigForBrand(HIDE_TABOOLA_ADS, vertical)
    && featureFlags?.['nbc-news-subscriptions']
    && !featureFlags?.['show-taboola-for-subscribers']
    && accessToken,
  );
}
